// frontend/src/pages/Products/Camera.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import cameraImg from '../Products/img/products-camera-content.webp';
import './css/products.css';

const IMAGE_SRC = cameraImg;

// 語言正規化（避免 zh-TW / zh-CN 取不到只註冊的 zh）
const normalizeLang = (lng) => {
  if (!lng) return 'en';
  const lower = lng.toLowerCase();
  if (lower.startsWith('zh')) return 'zh';
  return lower;
};

const ensureCameraI18n = (i18n) => {
  const ns = 'camera';
  // 同步語言正規化與 fallback
  const norm = normalizeLang(i18n.language);
  if (norm !== i18n.language) i18n.changeLanguage(norm);
  if (!i18n.options.fallbackLng) {
    i18n.options.fallbackLng = ['zh', 'en'];
  }

  if (!i18n.hasResourceBundle('en', ns)) {
    i18n.addResourceBundle('en', ns, {
      sectionTitle: 'Smart Cameras',
      sectionSubtitle: 'High-Speed • High-Resolution • Real-Time Processing',
      title: 'SMART CAMERAS',
      intro1: 'The latest generation of highly integrated smart cameras incorporates a high‑speed processing unit (FPGA) to ensure fast, accurate detection of all product defects, enabling system optimization for inspecting metal and plastic films, synthetic paper, fiberglass cloth, woven fabrics, coated nonwovens, glass, and more.',
      intro2: '',
      intro3: '',
      intro4: '',
      modelsTitle: 'Available Camera Models',
      model8kColor: '8K Color Line Scan Camera',
      model8kMono: '8K Monochrome Line Scan Camera',
      model16kColor: '16K Color Line Scan Camera',
      model16kMono: '16K Monochrome Line Scan Camera',
      featuresTitle: 'Key Capabilities',
      fHighSpeed: 'High-Speed Acquisition',
      fHighRes: 'High-Resolution Imaging',
      fRealTime: 'Real-Time On-Camera Processing',
      fFPGA: 'FPGA Parallel 2D Filters',
      fEthernet: 'Ethernet — No Frame Grabber',
      fStandalone: 'Standalone or Hybrid Processing',
      viewFull: 'View fullsize'
    });
  }
  if (!i18n.hasResourceBundle('zh', ns)) {
    i18n.addResourceBundle('zh', ns, {
      sectionTitle: '工業相機',
      sectionSubtitle: '高速 • 高解析 • 即時處理',
      title: '工業相機',
      intro1: '最新一代高度整合的智慧相機內建高速處理單元（FPGA）, 可以確保高速， 準確的檢測產品上的所有缺陷，利於系統優化以檢測金屬塑膠薄膜、 合成紙、 玻纖布、 編織布、 無紡布塗布、 玻璃等。',
      intro2: '',
      intro3: '',
      intro4: '',
      modelsTitle: '相機款式',
      model8kColor: '8K 彩色線掃描相機',
      model8kMono: '8K 黑白線掃描相機',
      model16kColor: '16K 彩色線掃描相機',
      model16kMono: '16K 黑白線掃描相機',
      featuresTitle: '核心能力',
      fHighSpeed: '高速擷取',
      fHighRes: '高解析影像',
      fRealTime: '即時機上處理',
      fFPGA: 'FPGA 平行 2D 濾波',
      fEthernet: '乙太網 無需擷取卡',
      fStandalone: '獨立或協同運算',
      viewFull: '查看原尺寸'
    });
  }
};

const Camera = () => {
  const { t, i18n } = useTranslation();
  // 改為同步確保首 render 可用資源
  ensureCameraI18n(i18n);

  return (
    <div>
      <section className="camera-section relative text-white py-20">
        <div className="absolute inset-0 bg-black bg-opacity-70" />
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-4">{t('camera:sectionTitle')}</h1>
          <p className="text-xl text-white">{t('camera:sectionSubtitle')}</p>
        </div>
      </section>

      <main style={{
        padding: 'clamp(1rem,2vw,2.5rem)',
        maxWidth: '1200px',
        margin: '0 auto',
        fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
        lineHeight: 1.6
      }}>
        <header style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem,4vw,2.6rem)', margin: 0, letterSpacing: '.5px' }}>
            {t('camera:title')}
          </h2>
        </header>

        <section style={{ marginBottom: '2rem' }}>
          <p style={{ marginBottom: '1.5rem', fontSize: '1rem', lineHeight: 1.8 }}>
            {t('camera:intro1')}
          </p>
        </section>

        <section style={{ marginBottom: '2.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', margin: '0 0 1rem', fontWeight: 600 }}>
            {t('camera:modelsTitle')}
          </h3>
          <div style={{
            display: 'grid',
            gap: '1rem',
            gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))'
          }}>
            {['model8kColor', 'model8kMono', 'model16kColor', 'model16kMono'].map(k => (
              <div key={k} style={{
                padding: '1.25rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,.15)',
                transition: 'transform 0.2s ease',
                cursor: 'default'
              }}>
                <strong style={{ display: 'block', fontSize: '1rem', marginBottom: '.5rem' }}>
                  {t(`camera:${k}`)}
                </strong>
              </div>
            ))}
          </div>
        </section>

        <section style={{
          display: 'grid',
          gap: '2rem',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%, 300px),1fr))',
          alignItems: 'start',
          marginBottom: '2.5rem'
        }}>
          <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
            <div style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0,0,0,.12)'
            }}>
              <img
                src={IMAGE_SRC}
                alt={t('camera:sectionTitle')}
                style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'cover' }}
                loading="lazy"
              />
            </div>
          </figure>
        </section>

        <section style={{ marginBottom: '3rem' }}>
          <h3 style={{ fontSize: '1.25rem', margin: '0 0 1rem', fontWeight: 600 }}>
            {t('camera:featuresTitle')}
          </h3>
          <div style={{
            display: 'grid',
            gap: '1rem',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))'
          }}>
            {['fHighSpeed','fHighRes','fRealTime','fFPGA','fEthernet','fStandalone'].map(k => (
              <div key={k} style={{
                padding: '1rem',
                background: '#f8f9fa',
                border: '1px solid #e2e6ea',
                borderRadius: '8px'
              }}>
                <strong style={{ display: 'block', fontSize: '.9rem' }}>{t(`camera:${k}`)}</strong>
                <span style={{
                  fontSize: '.65rem',
                  letterSpacing: '.5px',
                  color: '#888',
                  textTransform: 'uppercase'
                }}>{k}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Camera;